/* ============================================================
   SAM-198 — "Resources" section (editor v2, mock 3 v2; was "Units" /
   "Cabanas"). What gets booked: one row per physical cabana, table, tee
   start… with only its name and max guests. Each setup uses its own word
   (resourceWord in ../editorV2.ts). Prices live only in Pricing — the
   per-unit price overrides moved to Pricing's "Different price per …".

   Storage is unchanged: `service.settings.resources[] = {key, name,
   capacity, max_simultaneous_bookings, metadata}`, read by the booking
   engine sync (`functions/booking/engine_sync.py::_build_resource_payload`).
   `max_simultaneous_bookings` and the pricing metadata are kept untouched
   on every row (just not shown here). Presentation only — all state comes
   from useServiceEditor via the shared `Editor` type.
   ============================================================ */
import React from 'react';
import { Boxes, Plus, X } from 'lucide-react';
import { uniqueResourceKey } from '../../../components/ServiceResourcesEditor';
import { computeHeadcountWarning, formatHeadcountWarning } from '../headcountWarning';
import { Notice } from '../../../components/ui/Notice';
import { SectionCard, type Editor } from '../ServiceEditorPage';
import { unitsBelowMaxPax } from '../unitPricing';
import { capitalize, resourceWord } from '../editorV2';

interface UnitsSectionProps {
  editor: Editor;
  /** Kept for callers; the heading is always "Resources" now. */
  label?: string;
}

const cell = 'h-11 w-full rounded-[8px] border border-solid border-[var(--line)] bg-white px-3 text-[14px]';

export function UnitsSection({ editor }: UnitsSectionProps) {
  const { formData, setFormData, removeResource, updateResource } = editor;
  const warning = computeHeadcountWarning(formData.resources, formData.tier_groups);
  const belowMax = formData.price_unit === 'unit' ? unitsBelowMaxPax(formData, formData.resources) : [];
  const word = resourceWord(formData.subtype);
  const capWord = formData.subtype === 'teetime' ? 'Players' : 'Max guests';

  const add = () => {
    setFormData((prev) => {
      const name = `${capitalize(word.singular)} ${prev.resources.length + 1}`;
      const last = prev.resources[prev.resources.length - 1];
      return {
        ...prev,
        resources: [...prev.resources, {
          key: uniqueResourceKey(name, prev.resources),
          name,
          capacity: last?.capacity ?? '',
          max_simultaneous_bookings: '',
        }],
      };
    });
  };

  return (
    <SectionCard title={`Resources${formData.resources.length ? ` (${formData.resources.length})` : ''}`} icon={<Boxes size={14} />}>
      <p className="text-[13px] mb-4" style={{ color: 'var(--ink-500)' }}>
        What gets booked: one row per physical {word.singular}. Each one is booked once{' '}
        {formData.booking_type === 'day' ? 'per day' : 'per time slot'}. Prices are in Pricing.
      </p>

      {formData.resources.length === 0 ? (
        <p className="text-[13px] italic mb-3" style={{ color: 'var(--ink-400)' }}>
          1 pool (no named resources): the service is sold as one pool, limited by the total guests set in
          Booking &amp; hours. Add {word.plural} to book them one by one.
        </p>
      ) : (
        <div className="mb-3" role="table" aria-label="Resources">
          <div role="row" className="grid grid-cols-[minmax(0,1fr)_110px_40px] gap-2 text-[12px] font-semibold mb-1.5" style={{ color: 'var(--ink-500)' }}>
            <span role="columnheader">{capitalize(word.singular)}</span>
            <span role="columnheader">{capWord}</span>
            <span />
          </div>
          {formData.resources.map((r, index) => (
            <div key={r.key || index} role="row" className="grid grid-cols-[minmax(0,1fr)_110px_40px] gap-2 mb-2 items-center">
              <input
                className={cell} aria-label={`${capitalize(word.singular)} ${index + 1} name`}
                value={r.name} onChange={(e) => updateResource(index, 'name', e.target.value)}
              />
              <input
                className={cell} type="number" min={0} aria-label={`${capitalize(word.singular)} ${index + 1} ${capWord.toLowerCase()}`}
                value={r.capacity} placeholder="e.g. 6" onChange={(e) => updateResource(index, 'capacity', e.target.value)}
              />
              <button
                type="button" aria-label={`Remove ${r.name || word.singular}`} onClick={() => removeResource(index)}
                className="w-10 h-10 inline-flex items-center justify-center rounded-[8px] border-0 bg-transparent cursor-pointer"
                style={{ color: 'var(--ink-400)' }}
              >
                <X size={15} />
              </button>
            </div>
          ))}
        </div>
      )}

      <button type="button" className="btn ghost" onClick={add}>
        <Plus size={14} /> Add {word.singular}
      </button>

      {formData.subtype === 'teetime' && (
        <p className="text-[12px] mt-3" style={{ color: 'var(--ink-500)' }}>
          One row per starting tee, not per course: split starts (groups off the 1st and the 10th) need two.
        </p>
      )}

      {belowMax.length > 0 && (
        <Notice variant="warn" className="mt-3">
          {`Max pax is higher than the capacity of ${belowMax.map((u) => `${u.name} (${u.capacity} < ${u.max_pax})`).join(', ')}. A booking would pass pricing and be rejected by the booking engine.`}
        </Notice>
      )}

      {warning && (
        <Notice variant="warn" className="mt-3">
          {formatHeadcountWarning(warning)}
        </Notice>
      )}
    </SectionCard>
  );
}
